import React, { useState } from 'react';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckIcon from '@mui/icons-material/Check';
import { setStaffColor } from '@/services/taskService';

const UNASSIGNED = '#9CA3AF';

// Suggested accessible palette (from the brief). Admins can pick any of these.
const PALETTE = ['#2563EB', '#0D9488', '#EA580C', '#7C3AED', '#D97706', '#DB2777', '#0891B2', '#65A30D', '#DC2626', '#4F46E5'];

/**
 * Right sidebar: colour-coded team filter. Each row is a tick toggle controlling
 * whose task cards are shown on the board. Selected rows are highlighted with a
 * faded wash of the member's preferred colour. "Select all" toggles everyone.
 * Admins can click the swatch to assign a colour.
 */
const StaffLegend = ({ staff, counts, overdue, enabled, onToggle, onToggleAll, allSelected, isAdmin, onColorChanged }) => {
  const [picking, setPicking] = useState(null); // staff id whose swatch is open

  const handlePick = async (id, color) => {
    setPicking(null);
    try { await setStaffColor(id, color); onColorChanged && onColorChanged(); }
    catch (e) { console.error(e); }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-1.5 px-1 mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <GroupsOutlinedIcon style={{ fontSize: 16 }} className="text-[#002169]" />
          <h3 className="text-sm font-bold text-[#002169] truncate">Team</h3>
        </div>
        <button
          onClick={onToggleAll}
          className="flex-shrink-0 text-[11px] font-bold px-2 py-1 rounded-lg border border-gray-200 text-[#002169] hover:bg-[#002169] hover:text-white transition">
          {allSelected ? 'Clear all' : 'Select all'}
        </button>
      </div>

      <div className="space-y-1">
        {staff.map((s) => {
          const color = s.task_color || UNASSIGNED;
          const on = enabled.has(String(s.Id));
          const count = counts[String(s.Id)] || 0;
          const od = overdue[String(s.Id)] || 0;
          return (
            <div key={s.Id} role="button" tabIndex={0}
              onClick={() => onToggle(String(s.Id))}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(String(s.Id)); } }}
              title={on ? 'Hide their cards' : 'Show their cards'}
              style={on ? { backgroundColor: `${color}1A`, borderColor: `${color}59` } : undefined}
              className={`relative w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left cursor-pointer transition border
                ${on ? '' : 'border-transparent opacity-45 hover:opacity-80 hover:bg-gray-50'}`}>
              {/* Colour swatch (admin can change) */}
              <span
                role={isAdmin ? 'button' : undefined}
                onClick={(e) => { if (isAdmin) { e.stopPropagation(); setPicking(picking === s.Id ? null : s.Id); } }}
                title={isAdmin ? 'Change colour' : undefined}
                className={`flex-shrink-0 w-3.5 h-3.5 rounded-full ring-2 ring-white shadow ${isAdmin ? 'cursor-pointer hover:scale-110 transition' : ''}`}
                style={{ backgroundColor: color }} />

              {/* Name */}
              <span className={`flex-1 min-w-0 text-sm truncate ${on ? 'text-gray-800 font-semibold' : 'text-gray-500'}`}>
                {s.Name}
              </span>

              {/* Overdue badge */}
              {od > 0 && (
                <span title={`${od} overdue`} className="flex items-center gap-0.5 text-[10px] font-bold text-red-600 bg-red-100 rounded-full px-1.5 py-0.5 flex-shrink-0">
                  <WarningAmberIcon style={{ fontSize: 11 }} />{od}
                </span>
              )}
              {/* Week count */}
              <span className="text-[11px] font-semibold text-gray-400 flex-shrink-0 min-w-[16px] text-right">{count}</span>

              {/* Tick toggle */}
              <span
                style={on ? { backgroundColor: color, borderColor: color } : undefined}
                className={`flex-shrink-0 w-4 h-4 rounded flex items-center justify-center border ${on ? '' : 'border-gray-300 bg-white'}`}>
                {on && <CheckIcon style={{ fontSize: 12, color: '#fff' }} />}
              </span>

              {/* Colour picker popover */}
              {picking === s.Id && (
                <span onClick={(e) => e.stopPropagation()}
                  className="absolute right-0 top-8 z-20 bg-white border border-gray-200 rounded-xl shadow-xl p-2 grid grid-cols-5 gap-1.5 w-44">
                  {PALETTE.map((c) => (
                    <button key={c} type="button" onClick={(e) => { e.stopPropagation(); handlePick(s.Id, c); }}
                      className="w-6 h-6 rounded-full ring-2 ring-white shadow hover:scale-110 transition"
                      style={{ backgroundColor: c }} />
                  ))}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-gray-400 italic px-1 mt-2">Tick the people whose cards you want to see.</p>
    </div>
  );
};

export default StaffLegend;
